/**
 * FleetMap — the fleet composition of the map primitive.
 *
 * Basemaps, icons, sizing and recentring live in `components/map`; this file
 * only says what a fleet screen plots on top of them: trucks, their trails,
 * the zones of the corridor and — when the trip carries one — the planned
 * itinerary.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import MapSurface from './map/MapSurface';
import MapLegend from './map/MapLegend';
import TruckLayer from './map/TruckLayer';
import ZoneLayer from './map/ZoneLayer';
import AlertLayer from './map/AlertLayer';
import RouteLayer from './map/RouteLayer';
import { DEFAULT_CENTER, DEFAULT_ZOOM } from './map/layers';
import FleetMapControls, { MapRef, WheelGuard } from './map/FleetMapControls';
import { FLEET_LEGEND } from './map/status';
import { aUnePosition, cleVehicule } from './map/fleetRules';
import { classifyCorridorZones, parseRouteGeometry } from './map/geo';

export { MAP_LAYERS } from './map/layers';

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const DUREE_ASTUCE_MS = 1800;
const ASTUCE_MOLETTE = 'Cliquez sur la carte, ou Ctrl + molette, pour zoomer';
const ASTUCE_TACTILE = 'Deux doigts pour déplacer la carte';

export default function FleetMap({
  vehicles = EMPTY_ARRAY,
  selectedKey = null,
  onSelect,
  now,
  overlay = null,
  children,
  layer = 'satellite',
  showTrails = true,
  history = EMPTY_OBJECT, // { truck_id: [[lat,lng], ...] }
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
  className = '',
  geoLocations = EMPTY_ARRAY,
  routeGeometry = null,
  routePosition = null,
  alertes = EMPTY_ARRAY,
}) {
  const visibles = useMemo(() => vehicles.filter(aUnePosition), [vehicles]);

  const points = useMemo(
    () => visibles.map((v) => [Number(v.latitude), Number(v.longitude)]),
    [visibles],
  );

  const selected = useMemo(
    () => (selectedKey ? vehicles.find((v) => cleVehicule(v) === selectedKey) || null : null),
    [vehicles, selectedKey],
  );

  const selectedPos = selected && aUnePosition(selected)
    ? [Number(selected.latitude), Number(selected.longitude)]
    : null;

  const [map, setMap] = useState(null);
  const [astuce, setAstuce] = useState(null);
  const minuterie = useRef(null);
  const montrer = useCallback((texte) => {
    setAstuce(texte);
    if (minuterie.current) window.clearTimeout(minuterie.current);
    minuterie.current = window.setTimeout(() => setAstuce(null), DUREE_ASTUCE_MS);
  }, []);
  const montrerAstuce = useCallback(() => montrer(ASTUCE_MOLETTE), [montrer]);
  const montrerAstuceTactile = useCallback(() => montrer(ASTUCE_TACTILE), [montrer]);
  useEffect(() => () => window.clearTimeout(minuterie.current), []);

  const route = useMemo(() => parseRouteGeometry(routeGeometry), [routeGeometry]);

  // Zones only earn a "crossed / still ahead" verdict when an itinerary and a
  // position are both known; otherwise they stay neutral rather than guess.
  const zones = useMemo(
    () =>
      route.length > 1
        ? classifyCorridorZones(geoLocations, route, routePosition)
        : geoLocations,
    [geoLocations, route, routePosition],
  );

  const legend = useMemo(
    () =>
      route.length > 1
        ? [
            ...FLEET_LEGEND,
            { key: 'route-done', label: 'Parcouru', color: 'hsl(var(--cat-done))' },
            {
              key: 'route-left',
              label: 'Restant',
              color: 'hsl(var(--cat-route))',
              dashed: true,
            },
          ]
        : FLEET_LEGEND,
    [route.length],
  );

  return (
    <MapSurface
      layer={layer}
      center={center}
      zoom={zoom}
      className={className}
      mapClassName="h-full w-full rounded-lg"
      fitTo={points}
      focus={selectedPos}
      zoomControl={false}
      scrollWheelZoom={false}
      legend={<MapLegend items={legend} repliable />}
      overlay={
        <>
          <FleetMapControls
            map={map}
            vehicles={visibles}
            onSelect={onSelect}
            onGesteBloque={montrerAstuceTactile}
          />
          {astuce && (
            <div
              role="status"
              className="pointer-events-none absolute left-1/2 top-3 z-[450] -translate-x-1/2 rounded-lg border border-border bg-card/95 px-3 py-1.5 text-xs font-medium text-foreground shadow-sm"
            >
              {astuce}
            </div>
          )}
          {overlay}
        </>
      }
    >
      <MapRef onReady={setMap} />
      <WheelGuard onBlocked={montrerAstuce} />
      <ZoneLayer zones={zones} />
      <AlertLayer alertes={alertes} now={now} />
      {route.length > 1 && (
        <RouteLayer geometry={route} position={routePosition} />
      )}
      <TruckLayer
        vehicles={vehicles}
        history={history}
        showTrails={showTrails}
        selectedKey={selectedKey}
        onSelect={onSelect}
        now={now}
      />
      {children}
    </MapSurface>
  );
}
